import { ErrorHandler, Injectable, NgModule } from '@angular/core';
import { sentryDsn, sentryEnv } from '@env';
import * as Sentry from '@sentry/browser';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { SentryService } from './sentry.service';
import { appVersion } from './constants';
import { FireAuth, FirestoreService, fromRef } from 'ngfire';
import { User } from '@blockframes/model';
import { map, of, switchMap } from 'rxjs';
import { DocumentReference } from 'firebase/firestore';

@Injectable()
export class SentryErrorHandler implements ErrorHandler {
  constructor(
    private authService: FireAuth<User>,
    private firestore: FirestoreService,
    private sentryService: SentryService,
  ) {
    this.authService.user$.pipe(switchMap(u => {
      if (!u?.uid) return of(undefined);
      const ref = this.firestore.getRef(`users/${u.uid}`) as DocumentReference<User>;
      return fromRef(ref).pipe(map(snap => snap.data()));
    })).subscribe(user => {
      if (!user) {
        Sentry.configureScope(scope => {
          scope.setTag('appVersion', appVersion);
        });
        return;
      }

      Sentry.configureScope(scope => {
        scope.setUser({
          email: user.email,
          id: user.uid,
          username: `${user.firstName} ${user.lastName}`
        });
        scope.setTag('appVersion', appVersion);
      });
    });
  }

  handleError(error) {
    if (sentryEnv as unknown !== 'production') {
      console.error(error);
    }

    if (error?.message.includes('ChunkLoadError')) {
      this.sentryService.triggerError({ message: 'ChunkLoadError', bugType: 'network', location: 'global' });
    } else {
      Sentry.captureException(error.originalError || error);
    }
  }
}

// Init and add the Sentry ErrorHandler.
Sentry.init({
  dsn: sentryDsn,
  environment: sentryEnv,
  ignoreErrors: [
    // Firestore raises this whenever the user's device has no connectivity. It says nothing about
    // our code and there is nothing to fix, but it was the second most frequent error in
    // production — enough noise to hide the errors that do matter.
    // Matches both wordings Firestore uses: "Failed to get document(s) because the client is offline".
    'because the client is offline',
    // Raised when someone opens a page holding data their account cannot read. The security rules
    // are doing their job here — the read is refused, nothing leaks. In practice it is mostly the
    // team switching between accounts while supporting users, so it says nothing actionable.
    // Careful if you ever revisit this: a genuine rules regression would now be silent in Sentry,
    // and would only surface through a user report.
    'Missing or insufficient permissions',
  ],
});

@NgModule({
  imports: [MatSnackBarModule],
  providers: [{ provide: ErrorHandler, useClass: SentryErrorHandler }]
})
export class SentryModule { }
